<template>
  <div :class="bem.b()">
    <component
      :is="components[0]"
      ref="markDownRef"
      :class="bem.e('container')"
      v-bind="attrs"
      :locale="zh"
      :value="innerValue"
      :plugins="plugins"
      :upload-images="handleUploadImages"
      :sanitize="attrs.sanitize || sanitize"
      :mode="mode"
      @change="handleChange"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, markRaw, watch, useAttrs } from 'vue'
import Compressor from 'compressorjs'

//@ts-ignore
import { Editor } from '@bytemd/vue-next'

import gfm from '@bytemd/plugin-gfm'
import gemoji from '@bytemd/plugin-gemoji'
import highlight from '@bytemd/plugin-highlight' // 代码高亮
import frontmatter from '@bytemd/plugin-frontmatter' // 解析前题
import mediumZoom from '@bytemd/plugin-medium-zoom' // 缩放图片
import breaks from '@bytemd/plugin-breaks'
import mermaid from '@bytemd/plugin-mermaid'
import math from '@bytemd/plugin-math'
import zhHans from 'bytemd/locales/zh_Hans.json'
import gfmZhHans from '@bytemd/plugin-gfm/locales/zh_Hans.json'
// import 'bytemd/dist/index.css'
// import 'juejin-markdown-themes/dist/juejin.min.css' // 掘金同款样式
import { createNamespace } from '@ll-plus/utils'
import { llMarkdownEditorProps, llMarkdownEditorEmits } from './config'
const pluginsList = [
  gfm({ locale: gfmZhHans }),
  gemoji(),
  highlight(),
  frontmatter(),
  mediumZoom(),
  breaks(),
  mermaid(),
  math(),
]

defineOptions({ name: 'LlMarkdownEditor' })

const props = defineProps(llMarkdownEditorProps)
const emits = defineEmits(llMarkdownEditorEmits)

const bem = createNamespace('markdown-editor')

//@ts-ignore
const components = [Editor]
const markDownRef = ref()
const innerValue = ref(props.value)
const plugins = markRaw(pluginsList)
const zh = ref(zhHans)
const attrs = useAttrs()
watch(
  () => props.value,
  (n) => {
    innerValue.value = n
  }
)
const handleChange = async (val: string) => {
  emits('update:value', val)
  emits('change', val)
}
// 图片压缩
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      quality: 0.6, // 设置压缩质量
      maxWidth: 400, // 设置图片最大宽度
      maxHeight: 400, // 设置图片最大高度
      ...props.imageQuality,
      success(result) {
        resolve(result)
      },
      error(error) {
        reject(error)
      },
    })
  })
}

const handleUploadImages = async (files: File[]) => {
  try {
    const compressImages: File[] = []
    if (props.compress) {
      await Promise.all(
        files.map(async (file) => {
          const compressedFile: File = (await compressImage(file)) as File
          compressImages.push(compressedFile)
        })
      )
    }
    const showImages = await props.uploadImage(
      props.compress ? compressImages : files
    )

    return showImages.map((e, i) => ({
      title: files[i].name,
      url: e,
    }))
  } catch (error) {
    console.error('压缩和上传图像时出错:', error)
  }
}

const sanitize = (schema: any) => {
  // 创建配置的深拷贝，避免修改原始 schema
  const config = JSON.parse(JSON.stringify(schema))

  // 允许 data 协议在 src 属性中（用于 Base64 图片）
  if (config.protocols && config.protocols.src) {
    if (!config.protocols.src.includes('data')) {
      config.protocols.src.push('data')
    }
  }

  // 确保 img 标签有必要的属性支持
  if (config.attributes && config.attributes.img) {
    const imgAttributes = config.attributes.img

    // 确保包含 src 属性
    if (!imgAttributes.includes('src')) {
      imgAttributes.push('src')
    }

    // 添加 alt 属性用于更好的可访问性
    if (!imgAttributes.includes('alt')) {
      imgAttributes.push('alt')
    }

    // 添加 title 属性
    if (!imgAttributes.includes('title')) {
      imgAttributes.push('title')
    }
  }

  // 在全局属性中添加对 Base64 图片可能有用的属性
  if (config.attributes && config.attributes['*']) {
    const globalAttributes = config.attributes['*']

    // 确保包含 width 和 height 属性
    const neededAttributes = ['width', 'height']
    neededAttributes.forEach((attr) => {
      if (!globalAttributes.includes(attr)) {
        globalAttributes.push(attr)
      }
    })
  }

  // 添加自定义过滤逻辑来验证 Base64 图片
  config.customFilters = {
    // 对 img 标签的 src 属性进行额外验证
    img: (element: any) => {
      const src = element.getAttribute('src')

      // 如果是 data URI，验证是否为安全的图片类型
      if (src && src.startsWith('data:')) {
        // 只允许图片类型的 data URI
        const allowedImageTypes = [
          'image/jpeg',
          'image/jpg',
          'image/png',
          'image/gif',
          'image/webp',
          'image/svg+xml',
        ]

        const isSafeImageType = allowedImageTypes.some((type) =>
          src.startsWith(`data:${type}`)
        )

        if (!isSafeImageType) {
          // 移除不安全的 data URI
          element.removeAttribute('src')
        }

        // 可选：限制 Base64 数据大小（防止超大图片影响性能）
        const base64Match = src.match(/data:[^,]+,(.+)/)
        if (base64Match && base64Match[1]) {
          const base64Data = base64Match[1]
          // 限制 Base64 数据大小（例如 1MB）
          if (base64Data.length > 1.5 * 1024 * 1024) {
            // 约 1.5MB
            element.removeAttribute('src')
          }
        }
      }

      return element
    },
  }

  return config
}
</script>
